import React from "react";

import { config } from "@/config/environment";
import { useExperience } from "@/context/ExperienceContext";
import type { CommunicationSignupLead } from "@/types/CommunicationSignup";
import { getCommunicationSignupSourcePath } from "@/utils/consent/routing";

declare const grecaptcha: {
  enterprise: {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
  };
};

export function useNewsletterForm() {
  const { canonical, country } = useExperience();
  const [error, errorSet] = React.useState<boolean>(false);
  const [loading, loadingSet] = React.useState(false);
  const [success, successSet] = React.useState(false);

  const submitForm = React.useCallback(
    async (lead: CommunicationSignupLead) => {
      errorSet(false);
      loadingSet(true);
      successSet(false);

      try {
        const token = await grecaptcha.enterprise.execute(`${config.recaptchaSiteKey}`, {
          action: "submit"
        });

        const response = await fetch("/api/newsletter-signup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            country: country?.toUpperCase(),
            ...lead,
            email: lead.email.trim(),
            recaptchaToken: token,
            sourcePath: getCommunicationSignupSourcePath(canonical, window.location.pathname)
          })
        });

        if (response.ok) {
          successSet(true);
          errorSet(false);
        } else {
          errorSet(true);
        }
      } catch (e) {
        errorSet(true);
      } finally {
        loadingSet(false);
      }
    },
    [canonical, country]
  );

  return React.useMemo(
    () => ({
      error,
      loading,
      success,
      submitForm
    }),
    [error, loading, success, submitForm]
  );
}
